<template>
  <teleport to="#app">
    <div
      v-if="
        campaignStore.model?.state.isPopup &&
        !campaignStore.model?.state.isEditModeActive &&
        campaignStore.model?.state.edit?.enabled
      "
      class="lf-popup-play"
      @click.prevent="replayFlowAnimation"
    >
      <span class="lf-popup-play__icon icon-play-stroked"></span>
      <div class="lf-popup-play__label">Replay animation</div>
    </div>
  </teleport>

  <teleport v-if="didReceiveActiveModel" to=".flow--not-ejected">
    <transition name="fade" appear>
      <div
        v-if="activeFlowModel && activeFlowModel?.state.config.settings.state?.basic?.overlay?.enabled"
        class="flow__bg"
        :style="`background-color: ${activeFlowModel?.state.config.settings.state?.basic?.overlay?.backgroundColor};`"
        @click.stop="handleHidePopup"
      ></div>
    </transition>
  </teleport>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue';
import { inIframe, isSsr } from '@/src/utilities/Utilities';
import { events } from '@/src/services/events';
import { ActionType, SectionType } from '@/src/typings/enums/enums';
import { getSectionFrame } from '@/src/components/games/popup/Section';
import type { SectionBaseModel } from '@/src/components/layout/section/SectionBaseModel';
import type ActionsModel from '@/src/models/actions/ActionsModel';
import type { FlowpageModel } from '@/src/components/layout/FlowpageModel';
import type { IncomingIframeEvent, ListenerCallback } from '@/src/components/games/popup/Types';
import { PopupFrameIncomingEvent, PopupFrameOutgoingEvent } from '@/src/components/games/popup/Types';
import useDevice from '@/src/hooks/useDevice';
import { useCampaignStore } from '@/src/store/campaign';
import { useUtilityStore } from '@/src/store/utility';
import { useEditingStore } from '@/src/store/editing';

export default defineComponent({
  name: 'GamePopup',
  setup() {
    const utilityStore = useUtilityStore();
    const campaignStore = useCampaignStore();
    const editingStore = useEditingStore();

    const campaignModel = campaignStore.model;
    const listeners: { [key: string]: ListenerCallback[] } = {};
    const didReceiveActiveModel = ref(false);
    const activeFlowModel = ref<FlowpageModel | undefined>();

    let isReplayingFlow = false;

    const isDemo = computed(() => {
      if (isSsr()) {
        return false;
      }

      return utilityStore.url.includes('/campaign/view/demo');
    });

    /**
     * Popup is getting resized to fit with the popup content.
     * This can cause columns to shift and in turn shift the layout.
     *
     * To combat this we have added our simulation style &
     * assign the simulated class names to enforce the column distribution
     * according to the device viewing.
     */
    const simulationClass = computed(() => {
      const { isTablet, isMobile } = useDevice();

      if (!isDemo.value) {
        if (isTablet) {
          return 'site--simulate-tablet';
        } else if (isMobile) {
          return 'site--simulate-mobile';
        } else {
          return 'site--simulate-desktop';
        }
      }

      return '';
    });

    const onSectionBeforeEnterAnimation = async (model: SectionBaseModel) => {
      // Only send frame updates for flow pages.
      if (model.getSectionType() !== SectionType.FLOWPAGE) {
        return;
      }

      activeFlowModel.value = model;
      didReceiveActiveModel.value = true;
    };

    const onSectionReady = async (container: Element, model: SectionBaseModel) => {
      // Only send frame updates for flow pages.
      if (model.getSectionType() !== SectionType.FLOWPAGE) {
        return;
      }

      emitEvent(PopupFrameOutgoingEvent.SECTION_ENTER, getSectionFrame(container, model));

      if (inIframe()) {
        events.on('resizeFrame', () => {
          emitEvent(PopupFrameOutgoingEvent.SECTION_RESIZE, getSectionFrame(container, model));
        });
      }
    };

    const handleHidePopup = () => {
      campaignStore.flowId = undefined;
      emitEvent(PopupFrameOutgoingEvent.CLOSE_POPUP, null);
    };

    const onInvalidAction = (action: ActionsModel) => {
      // Popup specific action. This will just set the current flow page id to 0 to avoid any being shown.
      if (action.state.type === ActionType.CLOSE_POPUP) {
        campaignStore.flowId = undefined;

        emitEvent(PopupFrameOutgoingEvent.CLOSE_POPUP, {
          action: action.getData()
        });
      }
    };

    const onFlowPageChange = (from?: FlowpageModel, to?: FlowpageModel) => {
      if (from && to) {
        emitEvent(PopupFrameOutgoingEvent.SECTION_LEAVING);
      }
    };

    const onSectionBeforeLeave = () => {
      if (!campaignStore.flowId) {
        activeFlowModel.value = undefined;
      }
    };

    const onSectionAfterLeave = (model: SectionBaseModel) => {
      emitEvent(PopupFrameOutgoingEvent.SECTION_LEFT, {
        id: model.id
      });
    };

    const onPopoverShow = () => {
      emitEvent(PopupFrameOutgoingEvent.POPOVER_SHOW);
    };

    const onPopoverHide = () => {
      setTimeout(() => {
        emitEvent(PopupFrameOutgoingEvent.POPOVER_HIDE);
      }, 1000);
    };

    const onFormSubmitResponse = () => {
      emitEvent(PopupFrameOutgoingEvent.SUBMIT);
    };

    const replayFlowAnimation = () => {
      if (isReplayingFlow) {
        return;
      }

      isReplayingFlow = true;

      const currentFlowId = campaignStore.flowId;
      let leaveDuration = 500;
      let enterDuration = 500;

      if (currentFlowId) {
        const flowModel = campaignModel?.getFlowPageModel(currentFlowId);

        if (flowModel) {
          if (flowModel.state.config.settings.state.advanced?.transitions?.leaveDuration) {
            leaveDuration = flowModel.state.config.settings.state.advanced?.transitions?.leaveDuration;
          }

          if (flowModel.state.config.settings.state.advanced?.transitions?.leaveDelay) {
            leaveDuration += flowModel.state.config.settings.state.advanced.transitions.leaveDelay;
          }

          if (flowModel.state.config.settings.state.advanced?.transitions?.enterDuration) {
            enterDuration = flowModel.state.config.settings.state.advanced?.transitions?.enterDuration;
          }

          if (flowModel.state.config.settings.state.advanced?.transitions?.enterDelay) {
            enterDuration += flowModel.state.config.settings.state.advanced.transitions.enterDelay;
          }
        }
      }

      if (currentFlowId) {
        campaignStore.flowId = undefined;

        setTimeout(() => {
          campaignStore.flowId = currentFlowId;

          setTimeout(() => {
            isReplayingFlow = false;
          }, enterDuration);
        }, leaveDuration);
      }
    };

    const onMessage = (e: MessageEvent) => {
      if (typeof e.data === 'string' && e.data[0] === '{' && e.data.includes('lf-popup')) {
        const data = JSON.parse(e.data) as IncomingIframeEvent;

        if (data.type === 'lf-popup' && typeof listeners[data.name] !== 'undefined') {
          listeners[data.name].forEach((callback) => {
            callback(data.data);
          });

          delete listeners[data.name];
        }
      }
    };

    const emitEvent = (name: PopupFrameOutgoingEvent, data?: unknown) => {
      window.parent.postMessage(
        JSON.stringify({
          url: (window.location.href + '').replace(window.location.hash, ''),
          type: 'lf-popup',
          name,
          data,
          campaignId: campaignModel?.id
        }),
        '*'
      );
    };

    // Leaving this for now as we might need to for two-way communication.

    const waitForEvent = async <Response extends object>(event: PopupFrameIncomingEvent): Promise<Response> => {
      const promise = new Promise<Response>((resolve) => {
        if (typeof listeners[`${event}`] === 'undefined') {
          listeners[`${event}`] = [];
        }

        listeners[`${event}`].push(resolve);
      });

      return await promise;
    };

    editingStore.navigatorCategories = [
      {
        id: SectionType.FLOWPAGE,
        isActive: false,
        hide: false,
        label: 'Popup steps',
        strings: {
          plural: 'steps',
          single: 'step',
          addNew: 'Add new popup step'
        }
      },
      {
        id: SectionType.POPOVER,
        isActive: false,
        hide: false,
        label: 'Popovers',
        strings: {
          plural: 'popovers',
          single: 'popover',
          addNew: 'Add new popover'
        }
      }
    ];

    onBeforeMount(() => {
      events.on('invalidAction', onInvalidAction);
      events.on('sectionBeforeEnterAnimation', onSectionBeforeEnterAnimation);
      events.on('sectionReady', onSectionReady);
      events.on('flowPageChange', onFlowPageChange);
      events.on('sectionBeforeLeaveAnimation', onSectionBeforeLeave);
      events.on('sectionAfterLeave', onSectionAfterLeave);
      events.on('popoverShow', onPopoverShow);
      events.on('popoverHide', onPopoverHide);
      events.on('formSubmitResponse', onFormSubmitResponse);
      window.addEventListener('message', onMessage);
    });

    onBeforeUnmount(() => {
      events.off('invalidAction', onInvalidAction);
      events.off('sectionBeforeEnterAnimation', onSectionBeforeEnterAnimation);
      events.off('sectionReady', onSectionReady);
      events.off('flowPageChange', onFlowPageChange);
      events.off('sectionBeforeLeaveAnimation', onSectionBeforeLeave);
      events.off('sectionAfterLeave', onSectionAfterLeave);
      events.off('popoverShow', onPopoverShow);
      events.off('popoverHide', onPopoverHide);
      events.off('formSubmitResponse', onFormSubmitResponse);
      window.removeEventListener('message', onMessage);
    });

    const onBeforeEnter = async () => {
      /**
       * The backend iframes in the campaign when previewing and sets the parameter "template=overlay"
       * When we have this, we dont get a height, which causes it to fail
       * That is why this check is in place
       */
      if (inIframe() && utilityStore.url && !utilityStore.url.includes('template=overlay')) {
        emitEvent(PopupFrameOutgoingEvent.REQUEST_WINDOW_HEIGHT);
        const windowHeight = await waitForEvent<{ height: number }>(PopupFrameIncomingEvent.RESPONSE_WINDOW_HEIGHT);
        utilityStore.overwrittenWindowHeight = windowHeight.height;
      }
    };

    if (!isSsr()) {
      watch(
        simulationClass,
        () => {
          if (simulationClass.value) {
            utilityStore.removeBodyClasses([
              'site--simulate-desktop',
              'site--simulate-tablet',
              'site--simulate-mobile'
            ]);

            utilityStore.addBodyClass(simulationClass.value);
          }
        },
        {
          immediate: true
        }
      );
    }

    return {
      handleHidePopup,
      replayFlowAnimation,
      campaignStore,
      onBeforeEnter,
      activeFlowModel,
      didReceiveActiveModel
    };
  }
});
</script>

<style lang="scss">
@import '../../../assets/scss/base/variables';
@import '../../../assets/scss/utilities/animations';

@import '../../../../node_modules/bootstrap/scss/functions';
@import '../../../../node_modules/bootstrap/scss/variables';
@import '../../../../node_modules/bootstrap/scss/mixins';

$edit-simulate-prefix: ' .campaign ';

@import '../../../assets/scss/edit/simulate-device/main';

.site.site--simulate-tablet.demo-mode .site-editor__viewer,
.site.site--simulate-mobile.demo-mode .site-editor__viewer {
  background-color: #fff !important;
}

.site--simulate-tablet .container,
.site--simulate-mobile .container {
  max-width: none !important;
}

html body,
html body.site--desktop,
html body.site--tablet,
html body.site--mobile,
html {
  background-color: transparent !important;
  height: 100vh;
  overflow: visible;
}

.site--simulate-desktop .container {
  max-width: 1140px !important;
}

.site-editor__viewer {
  background: transparent !important;

  // This fix issue with editor navigator overlapping the popover when it's align right
  transform: translate(0);
}

.campaign__pages {
  transition: none !important;
  opacity: 1 !important;
  animation: none !important;
}

.gameflow > .flow {
  position: fixed;
  left: 0;
  top: 0;
  width: 100% !important;
  height: 100% !important;
  overflow: hidden;
}

.gameflow > .flow > .positioner {
  display: inline-block !important;
  position: absolute !important;
  left: auto !important;
  bottom: auto !important;
  right: auto !important;
  top: auto !important;
  max-height: 100%;
}

.gameflow > .flow > .positioner--position-bottom-left {
  left: 0 !important;
  bottom: 0 !important;
}

.gameflow > .flow > .positioner--position-bottom-center {
  left: 50% !important;
  bottom: 0 !important;
  transform: translate(-50%) translateZ(0) !important;
}

.gameflow > .flow > .positioner--position-bottom-right {
  right: 0 !important;
  bottom: 0 !important;
}

.gameflow > .flow > .positioner--position-center-left {
  left: 0 !important;
  top: 50% !important;
  transform: translateY(-50%) translateZ(0) !important;
}

.gameflow > .flow > .positioner--position-center-center {
  left: 50% !important;
  top: 50% !important;
  transform: translate(-50%, -50%) translateZ(0) !important;
}

.gameflow > .flow > .positioner--position-center-right {
  right: 0 !important;
  top: 50% !important;
  transform: translateY(-50%) translateZ(0) !important;
}

.gameflow > .flow > .positioner--position-top-left {
  left: 0 !important;
  top: 0 !important;
}

.gameflow > .flow > .positioner--position-top-center {
  left: 50% !important;
  top: 0 !important;
  transform: translate(-50%) translateZ(0) !important;
}

.gameflow > .flow > .positioner--position-top-right {
  right: 0 !important;
  top: 0 !important;
}

.gameflow > .flow > .positioner > .flow__page--leave {
  position: relative !important;
}

// TODO: Investigate if we really need this.
.gameflow > .flow > .positioner > .section:not(.animated) {
  transform: translateZ(0) !important;
}

.flow__bg {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  background-color: transparent;
  transition: background-color 375ms linear;
}

@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  .grid__col,
  .grid__col-container,
  .col {
    position: static !important;
  }

  .grid__col-container--has-background {
    position: relative !important;
  }

  html .grid__addon.grid__addon--position-fixed {
    position: absolute !important;
  }
}
html .grid__addon--position-fixed {
  visibility: hidden;
  animation-fill-mode: forwards;
  animation-name: fadeInFixed;
  animation-delay: 875ms;
  animation-duration: 375ms;
  animation-iteration-count: 1;
}

@keyframes fadeInFixed {
  0% {
    opacity: 0;
    visibility: visible;
  }
  100% {
    opacity: 1;
    visibility: visible;
  }
}

/** Edit mode related CSS **/
.campaign__add-section {
  display: none;
}

.site--editmode-on .section.flow__page {
  min-height: 50px;
}

.grid__addon--gameflow > .grid__addon-invalid-message {
  display: none !important;
}

.site--editmode-on .site-editor {
  min-width: 0 !important;
}

.gameflow__empty {
  text-align: center;
  padding: 3rem 0;
}

.lf-popup-play {
  position: fixed;
  right: 90px;
  bottom: 30px;
  display: block;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #131313;
  line-height: 42px;
  font-size: 16px;
  color: #ffffff;
  text-align: center;
  cursor: pointer;
  user-select: none;
}

.lf-popup-play .icon-play-stroked::before {
  margin-left: 4px;
}

.lf-popup-play__label {
  display: block;
  position: absolute;
  top: 50%;
  right: calc(100% + 10px);
  transform: translateY(-50%);
  padding: 4px 5px;
  background: #202020;
  border-radius: 4px;
  font-family: 'Gilroy', Helvetica, Arial, sans-serif;
  font-size: 11px;
  line-height: 11px;
  letter-spacing: 0;
  white-space: nowrap;
  color: #d8d8d8;
  opacity: 0;
  transition: opacity 150ms linear;
  pointer-events: none;
}

.lf-popup-play:hover .lf-popup-play__label {
  opacity: 1;
}

.site--editmode-on .grid__addon--position-absolute,
.site--editmode-on .grid__addon--position-fixed {
  cursor: default !important;
}
</style>
